import { useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { Button } from '../components/Button';
import { useGoogleSignIn } from './useGoogleSignIn';
import { fetchInviteBranding } from '../api';
import { identify, type IdentifyMethod } from '../auth';
import { useTheme } from '../theme';
import { isDevEnvironment } from '../environment';
import appIcon from '../assets/app-icon.png';
import './LoginPage.css';
import { useTenant } from '../tenant';

/**
 * Sign-in, identifier first.
 *
 * One box, then one challenge. The first screen takes whatever a person has —
 * an email, a phone number, or an issued login id like `1042.teoler` — and asks
 * the server what that identifier accepts. The second screen asks for exactly
 * that one thing: a password, or a code, or the provider that made the account.
 *
 * The screen it replaced made the person do that routing themselves: an email
 * box beside a password box, a "sign in with phone instead" link, and a
 * Google-only account discovering it was Google-only by being told its password
 * was wrong.
 *
 * ## What screen 1 will and won't guess
 *
 * It reads digits, and nothing else. A complete phone number is the one thing a
 * client may resolve alone, because no account carries an all-digit login id of
 * that length. Everything else — email, login id — gets no hint at all, for two
 * reasons: the client genuinely cannot know (an address may be a password
 * account, a Google account, or no account), and naming the *kind* of account
 * would tell anyone who can type that a given id is a school-provisioned one.
 *
 * See docs/FeatureDesigns/identifier-first-signin-target.md.
 */

export interface LoginPageProps {
  subtitle?: string;
  /** Heading on the sign-in screens; defaults to "BuzzBrain". Ignored on a
   *  tenant URL, where the tenant's name is the heading. */
  title?: string;
  /** Heading on the register screens; defaults to `title`. */
  registerTitle?: string;
  /** A small link above the card's heading — e.g. back to a landing page. */
  topLink?: ReactNode;
  onLogin: (email: string, password: string) => Promise<void>;
  onGoogleLogin: (credential: string) => Promise<void>;
  /** Native Sign in with Apple (shown only when provided — e.g. inside the iOS shell). */
  onAppleLogin?: () => Promise<void>;
  /** Native Google sign-in (shown only when provided — e.g. inside the iOS shell,
   *  where Google's OAuth is blocked in the embedded WebView). When set, the GSI
   *  iframe button is suppressed in favour of this native button. */
  onGoogleNativeLogin?: () => Promise<void>;
  googleClientId?: string;
  /** Under the first sign-in screen. */
  footer?: ReactNode;
  /** Under the first sign-up screen, after "Already have an account?" — the
   *  way to the other kind of account. */
  registerFooter?: ReactNode;
  headerExtra?: ReactNode;
  submitLabel?: string;
  mode?: 'login' | 'register';
  onRegister?: (name: string, email: string, password: string, verificationToken: string) => Promise<void>;
  onSendOtp?: (identifier: string, purpose: 'register' | 'reset-password' | 'login', channel?: 'email' | 'phone') => Promise<void>;
  onVerifyOtp?: (identifier: string, code: string, purpose: 'register' | 'reset-password' | 'login', channel?: 'email' | 'phone') => Promise<string>;
  onResetPassword?: (verificationToken: string, newPassword: string) => Promise<void>;
  /** Master switch resolved by the app from GET /auth/phone-login-available. */
  phoneLoginEnabled?: boolean;
  /** Passwordless phone sign-in: exchange a verified `login` token for a session. */
  onPhoneLogin?: (phone: string, verificationToken: string) => Promise<void>;
  /** Phone signup: create the account after phone OTP. The account is OTP-only —
   *  no password is collected here, and one can be set later from Settings. */
  onPhoneRegister?: (name: string, phone: string, verificationToken: string) => Promise<void>;
  defaultEmail?: string;
  emailDisabled?: boolean;
  registerSubtitle?: string;
  /** Tenant name carried by the invite link, for the first screen — nothing has
   *  been verified yet, so this is a HINT and never the last word: it comes off
   *  a URL anyone can edit. From the OTP step on, the server's answer for the
   *  email replaces it. */
  inviteBrandName?: string;
}

const MIN_PASSWORD_LENGTH = 6;

type FlowStep =
  | 'identify'             // one box: email, phone or login id
  | 'password'             // screen 2 — the account has a password
  | 'otp'                  // screen 2 — a code went to a verified phone
  | 'provider'             // screen 2 — Google/Apple made it; there is no password
  | 'register-identifier'  // sign up: email or phone → send code
  | 'register-otp'
  | 'register-details'     // name (+ password, required for email signups)
  | 'forgot-otp'
  | 'forgot-password';

/** Local phone number stripped to digits (India, +91 added server-side). */
function cleanPhoneDigits(raw: string): string {
  return raw.replace(/\D/g, '').replace(/^0/, '').replace(/^91(?=\d{10}$)/, '');
}

/** Digits and phone punctuation only — "was this person typing a number", not
 *  "is it a valid one". Mirrors `looksLikePhoneInput` on the server. */
function looksLikePhone(raw: string): boolean {
  const s = raw.trim();
  return !!s && /^[+\d\s\-()]+$/.test(s) && s.replace(/\D/g, '').length >= 4;
}

/** A country that isn't India. Sign-in can only deliver codes to +91. */
function isForeignNumber(raw: string): boolean {
  const s = raw.trim();
  if (!s.startsWith('+')) return false;
  const digits = s.replace(/\D/g, '');
  return digits.length > 0 && !digits.startsWith('91');
}

const FOREIGN_NUMBER_MESSAGE =
  'We can only send codes to Indian numbers right now. Sign in with your email instead, or use Google.';

export function LoginPage({
  subtitle,
  title,
  registerTitle,
  topLink,
  onLogin,
  onGoogleLogin,
  onAppleLogin,
  onGoogleNativeLogin,
  googleClientId = '',
  footer,
  registerFooter,
  headerExtra,
  submitLabel,
  mode = 'login',
  onRegister,
  onSendOtp,
  onVerifyOtp,
  onResetPassword,
  phoneLoginEnabled,
  onPhoneLogin,
  onPhoneRegister,
  defaultEmail,
  emailDisabled,
  registerSubtitle,
  inviteBrandName,
}: LoginPageProps) {
  // Pre-login tenant branding. A tenant URL must not show the BuzzBrain mark —
  // that misattributes whose product the login page is. Resolved from the URL by
  // TenantGate, so it is available before there is any session; the reserved
  // scopes never resolve, so a non-null tenant here is always a real customer.
  const { tenant, loading: tenantLoading } = useTenant();

  /**
   * May someone create an account from *this* URL?
   *
   * A tenant URL is invite-only: membership comes from an invite row, so a
   * self-signup here would produce a public-tenant account under a school's
   * brand — the URL and the resulting account disagreeing. Hiding the "Register
   * here" link is not enough, because `?register=1` is a second way in.
   *
   * Completing an invite is always allowed — that is the one path that *does*
   * put someone in the tenant, and it is authorised by the invite row rather
   * than by the URL.
   */
  const hasInvite = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).has('invite');
  const selfRegistrationBlocked = !hasInvite
    && (tenantLoading || (!!tenant && !tenant.allowsSelfRegistration));

  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  /** WRONG_TENANT_URL carries the shared host to try instead — see
   *  auth/errors.ts. Without it the refusal is a dead end. */
  const [errorSignInUrl, setErrorSignInUrl] = useState<string | null>(null);
  /** WRONG_APP names its button (see wrongApp.ts). */
  const [errorSignInLabel, setErrorSignInLabel] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<FlowStep>(mode === 'register' ? 'register-identifier' : 'identify');

  /** What was typed on screen 1, as the server read it back. Every later step
   *  works from this rather than re-reading the field, which is gone by then. */
  const [identifier, setIdentifier] = useState(defaultEmail || '');
  /** Local 10 digits when the identifier is a phone; '' otherwise. */
  const [otpPhone, setOtpPhone] = useState('');
  /** What screen 2 may offer, from `/auth/identify`. */
  const [methods, setMethods] = useState<IdentifyMethod[]>([]);
  const [verificationToken, setVerificationToken] = useState('');
  /** True when registration was reached by an identifier that matched nothing,
   *  rather than chosen from the footer. The screen has to say which. */
  const [arrivedFromMiss, setArrivedFromMiss] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /** Live shape of the identifier field, for the phone hint only. */
  const [typed, setTyped] = useState(defaultEmail || '');
  /** The register field is its own box on its own step, so it keeps its own
   *  value — sharing `typed` would let a sign-in attempt decide the register
   *  button's label. */
  const [registerTyped, setRegisterTyped] = useState(defaultEmail || '');

  // Who invited you, resolved from the identifier itself. Failure is silent: an
  // unbranded sign-up screen is the correct fallback, and a sign-in must never
  // be blocked by a cosmetic lookup.
  const [inviteBranding, setInviteBranding] = useState<{ brandName?: string; logoUrl?: string }>({});

  useEffect(() => {
    if (!identifier || !identifier.includes('@')) { setInviteBranding({}); return; }
    let live = true;
    fetchInviteBranding(identifier)
      .then(b => { if (live) setInviteBranding(b); })
      .catch(() => { if (live) setInviteBranding({}); });
    return () => { live = false; };
  }, [identifier]);

  // Three tenant signals, strongest first. A tenant URL outranks everything:
  // the visitor is standing on that tenant's front door whether or not they
  // were invited. Failing that the invite decides — the server's answer for a
  // committed email first, and only then the link's `?tenant=` hint, so a
  // hand-edited value cannot outlast the screen where nothing had been checked.
  const branding = tenant
    ? { brandName: tenant.name, logoUrl: tenant.branding?.logoUrl }
    : inviteBranding.brandName
      ? inviteBranding
      : (inviteBrandName ? { brandName: inviteBrandName } : {});

  const clearError = () => { setError(null); setErrorCode(null); setErrorSignInUrl(null); setErrorSignInLabel(null); };
  const showError = (err: unknown, fallback: string) => {
    setError(err instanceof Error ? err.message : fallback);
    // ApiError carries detailWord; fall back to undefined for plain Errors.
    const code = (err && typeof err === 'object' && 'detailWord' in err) ? (err as { detailWord?: unknown }).detailWord : undefined;
    setErrorCode(typeof code === 'string' ? code : null);
    const ctx = (err && typeof err === 'object' && 'context' in err)
      ? (err as { context?: Record<string, unknown> }).context : undefined;
    const url = ctx?.signInUrl;
    setErrorSignInUrl(typeof url === 'string' ? url : null);
    const label = ctx?.signInLabel;
    setErrorSignInLabel(typeof label === 'string' ? label : null);
  };

  // Sync step with parent mode prop changes (e.g. footer toggle)
  useEffect(() => {
    if (mode === 'register' && (step === 'identify' || step === 'password' || step === 'otp' || step === 'provider')) {
      setStep('register-identifier');
      clearError(); setSuccess(null);
    } else if (mode === 'login' && step.startsWith('register')) {
      setStep('identify');
      clearError(); setSuccess(null);
    }
  }, [mode]);

  const isRegisterFlow = step.startsWith('register');
  const isForgotFlow = step.startsWith('forgot');
  // The phone path needs the app to have enabled it AND wired the handlers.
  const showPhone = !!(phoneLoginEnabled && onSendOtp && onVerifyOtp && (onPhoneLogin || onPhoneRegister));

  const startCooldown = () => {
    setCooldown(30);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => () => { if (cooldownRef.current) clearInterval(cooldownRef.current); }, []);

  const handleGoogleResponse = useCallback(async (response: { credential: string }) => {
    clearError();
    setIsLoading(true);
    try {
      await onGoogleLogin(response.credential);
    } catch (err) {
      showError(err, 'Google sign-in failed');
    } finally {
      setIsLoading(false);
    }
  }, [onGoogleLogin]);

  const { effectiveTheme } = useTheme();
  // In the shell, Google's GSI iframe can't complete OAuth, so suppress it and use
  // the native button instead: pass '' so the hook never initialises GSI.
  const gsiClientId = onGoogleNativeLogin ? '' : googleClientId;
  const googleButtonRef = useGoogleSignIn(gsiClientId, handleGoogleResponse, effectiveTheme);

  const handleGoogleNative = useCallback(async () => {
    if (!onGoogleNativeLogin) return;
    setError(null);
    setIsLoading(true);
    try {
      await onGoogleNativeLogin();
    } catch (err) {
      showError(err, 'Google sign-in failed');
    } finally {
      setIsLoading(false);
    }
  }, [onGoogleNativeLogin]);

  const handleAppleLogin = useCallback(async () => {
    if (!onAppleLogin) return;
    setError(null);
    setIsLoading(true);
    try {
      await onAppleLogin();
    } catch (err) {
      showError(err, 'Apple sign-in failed');
    } finally {
      setIsLoading(false);
    }
  }, [onAppleLogin]);

  const switchToRegister = () => {
    clearError(); setSuccess(null);
    // Chosen, not landed on: an empty form, and no explanation owed.
    setArrivedFromMiss(false);
    setStep('register-identifier');
  };

  /** Back to one box. Everything the previous answer decided is discarded —
   *  a different identifier gets a different screen 2. */
  const switchToIdentify = () => {
    clearError(); setSuccess(null);
    setStep('identify');
    setMethods([]);
    setVerificationToken('');
    setArrivedFromMiss(false);
  };

  // ── Screen 1: identify ──────────────────────────────────────────────

  /**
   * Ask the server what this identifier accepts, then draw screen 2.
   *
   * The one branch decided locally is the foreign number, because no lookup can
   * improve on it: we cannot deliver there whether or not the account exists.
   */
  const handleIdentifySubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearError(); setSuccess(null);
    const raw = ((new FormData(e.currentTarget).get('identifier') as string) || defaultEmail || '').trim();
    if (!raw) return;

    if (isForeignNumber(raw)) {
      setError(FOREIGN_NUMBER_MESSAGE);
      return;
    }

    setIsLoading(true);
    try {
      const result = await identify(raw);
      if (result.unreachable === 'country-not-supported') {
        setError(FOREIGN_NUMBER_MESSAGE);
        return;
      }

      const phoneDigits = looksLikePhone(raw) ? cleanPhoneDigits(raw) : '';
      setIdentifier(result.normalized || raw);
      setOtpPhone(phoneDigits);
      setMethods(result.methods);

      // No account. Carry what was typed into sign-up rather than making them
      // read an error and type it again.
      if (result.methods.includes('register')) {
        setArrivedFromMiss(true);
        // `selfRegistrationBlocked` renders the "not open here" panel from the
        // same step, so both cases land in one place.
        setStep('register-identifier');
        setSuccess(null);
        return;
      }

      // A code beats a password when they typed the number it would go to.
      if (result.methods[0] === 'otp' && showPhone && phoneDigits.length === 10) {
        await onSendOtp?.(phoneDigits, 'login', 'phone');
        setStep('otp');
        setSuccess('Verification code sent to your phone');
        startCooldown();
        return;
      }

      if (result.methods.includes('password')) { setStep('password'); return; }
      if (result.methods.includes('google') || result.methods.includes('apple')) { setStep('provider'); return; }
      if (result.methods.includes('otp') && showPhone && phoneDigits.length === 10) {
        await onSendOtp?.(phoneDigits, 'login', 'phone');
        setStep('otp');
        startCooldown();
        return;
      }

      // `unknown`, or nothing we can offer: the password form gives the real
      // error, which is more use than a guess made here.
      setStep('password');
    } catch (err) {
      showError(err, 'Could not continue. Try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Screen 2: password ──────────────────────────────────────────────

  const handlePasswordSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearError();
    setIsLoading(true);
    const password = new FormData(e.currentTarget).get('password') as string;
    try {
      await onLogin(identifier, password);
    } catch (err) {
      showError(err, 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Screen 2: one-time code (sign-in) ───────────────────────────────

  const handleOtpSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearError(); setSuccess(null);
    setIsLoading(true);
    const code = ((new FormData(e.currentTarget).get('otp') as string) || '').trim();
    try {
      const token = await onVerifyOtp?.(otpPhone, code, 'login', 'phone');
      if (token) await onPhoneLogin?.(otpPhone, token);
    } catch (err) {
      showError(err, 'Invalid verification code');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Forgot password ─────────────────────────────────────────────────

  /** The identifier is already known, so this skips straight to sending a code. */
  const startForgot = async () => {
    clearError(); setSuccess(null);
    setIsLoading(true);
    const channel = otpPhone ? 'phone' : 'email';
    try {
      await onSendOtp?.(channel === 'phone' ? otpPhone : identifier, 'reset-password', channel);
      setStep('forgot-otp');
      setSuccess(channel === 'phone' ? 'Code sent to your phone' : 'Code sent to your email');
      startCooldown();
    } catch (err) {
      showError(err, 'Failed to send verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotOtpSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearError(); setSuccess(null);
    setIsLoading(true);
    const code = ((new FormData(e.currentTarget).get('otp') as string) || '').trim();
    const channel = otpPhone ? 'phone' : 'email';
    try {
      const token = await onVerifyOtp?.(channel === 'phone' ? otpPhone : identifier, code, 'reset-password', channel);
      if (token) {
        setVerificationToken(token);
        setStep('forgot-password');
      }
    } catch (err) {
      showError(err, 'Invalid verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPasswordSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearError(); setSuccess(null);
    const formData = new FormData(e.currentTarget);
    const newPassword = formData.get('password') as string;
    const confirmPassword = formData.get('confirmPassword') as string;
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setErrorCode(null);
      return;
    }
    setIsLoading(true);
    try {
      await onResetPassword?.(verificationToken, newPassword);
      setSuccess('Password reset. Sign in with your new password.');
      setStep('password');
      setVerificationToken('');
    } catch (err) {
      showError(err, 'Password reset failed');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Register ────────────────────────────────────────────────────────

  const handleRegisterSendOtp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearError(); setSuccess(null);
    // From a miss the identifier is shown as a chip, not a field — the value
    // then comes from what screen 1 already resolved. An invite link renders
    // it as a DISABLED field, which the browser leaves out of FormData, so the
    // prefilled value has to be read back the same way screen 1 reads it.
    const typedRaw = (new FormData(e.currentTarget).get('identifier') as string) || '';
    const raw = (typedRaw || (arrivedFromMiss ? (otpPhone || identifier) : '') || defaultEmail || '').trim();
    if (!raw) return;

    if (isForeignNumber(raw)) { setError(FOREIGN_NUMBER_MESSAGE); return; }

    const phoneDigits = looksLikePhone(raw) ? cleanPhoneDigits(raw) : '';
    const usePhone = !!phoneDigits && showPhone;
    if (looksLikePhone(raw) && !usePhone) {
      setError('Sign up with your email address.');
      return;
    }
    if (usePhone && phoneDigits.length !== 10) {
      setError('Enter a valid 10-digit mobile number');
      return;
    }

    setIsLoading(true);
    try {
      await onSendOtp?.(usePhone ? phoneDigits : raw, 'register', usePhone ? 'phone' : 'email');
      setIdentifier(usePhone ? raw : raw.toLowerCase());
      setOtpPhone(usePhone ? phoneDigits : '');
      setStep('register-otp');
      setSuccess(usePhone ? 'Verification code sent to your phone' : 'Verification code sent to your email');
      startCooldown();
    } catch (err) {
      showError(err, 'Failed to send verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegisterVerifyOtp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearError(); setSuccess(null);
    setIsLoading(true);
    const code = ((new FormData(e.currentTarget).get('otp') as string) || '').trim();
    const channel = otpPhone ? 'phone' : 'email';
    try {
      const token = await onVerifyOtp?.(channel === 'phone' ? otpPhone : identifier, code, 'register', channel);
      if (token) {
        setVerificationToken(token);
        setStep('register-details');
        setSuccess(channel === 'phone' ? `Phone verified: +91 ${otpPhone}` : `Email verified: ${identifier}`);
      }
    } catch (err) {
      showError(err, 'Invalid verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegisterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearError(); setSuccess(null);
    setIsLoading(true);
    const formData = new FormData(e.currentTarget);
    const name = formData.get('name') as string;
    try {
      if (otpPhone) {
        await onPhoneRegister?.(name, otpPhone, verificationToken);
      } else {
        const password = ((formData.get('password') as string) || '').trim();
        await onRegister?.(name, identifier, password, verificationToken);
      }
    } catch (err) {
      showError(err, 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Resend, shared by every code step ───────────────────────────────

  const resendPurpose = (): 'register' | 'reset-password' | 'login' =>
    isRegisterFlow ? 'register' : isForgotFlow ? 'reset-password' : 'login';

  const handleResend = async () => {
    clearError(); setSuccess(null);
    setIsLoading(true);
    const channel = otpPhone ? 'phone' : 'email';
    try {
      await onSendOtp?.(channel === 'phone' ? otpPhone : identifier, resendPurpose(), channel);
      setSuccess('New verification code sent');
      startCooldown();
    } catch (err) {
      showError(err, 'Failed to resend code');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Subtitle resolution ────────────────────────────────────────────

  const resolvedSubtitle = (() => {
    switch (step) {
      case 'identify': return subtitle;
      case 'password': return 'Enter your password';
      case 'otp': return 'Enter the code we texted you';
      case 'provider': return methods.includes('apple')
        ? 'This account signs in with Apple'
        : 'This account signs in with Google';
      case 'register-identifier': return registerSubtitle || 'Create your account';
      case 'register-otp': return 'Enter verification code';
      case 'register-details': return 'Complete your account';
      case 'forgot-otp': return 'Enter verification code';
      case 'forgot-password': return 'Choose a new password';
      default: return subtitle;
    }
  })();

  /**
   * The line under the identifier box.
   *
   * Digits only, and only once there are ten of them. See the note at the top
   * of this file for why an email or a login id gets nothing: the client cannot
   * know, and guessing out loud would disclose how someone's account was
   * provisioned.
   */
  const identifierHint = (() => {
    if (!showPhone || !looksLikePhone(typed)) return null;
    if (isForeignNumber(typed)) return FOREIGN_NUMBER_MESSAGE;
    const digits = cleanPhoneDigits(typed);
    // Nothing until the number is complete. A guess about what someone is
    // half-way through typing is noise they have to read and dismiss, and it
    // says nothing they do not already know — they are typing their own number.
    return digits.length === 10 ? `We'll text a one-time code to +91 ${digits}.` : null;
  })();

  /**
   * Is there somewhere to send a code yet?
   *
   * The button label is where this flow commits, so it may only promise a code
   * once one could actually go out: a complete Indian mobile number, or an
   * address with the shape of an address. Anything less is still "Continue",
   * because the field might yet become a login id, a half-typed email, or a
   * number we cannot reach.
   */
  const canSendCodeTo = (value: string): boolean => {
    const v = value.trim();
    if (!v) return false;
    if (looksLikePhone(v)) {
      return showPhone && !isForeignNumber(v) && cleanPhoneDigits(v).length === 10;
    }
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  };

  /** Sign-in takes a login id too, which gets no code — so only a phone
   *  commits here. */
  const identifyCta = showPhone && looksLikePhone(typed) && canSendCodeTo(typed)
    ? 'Send code'
    : (submitLabel ?? 'Continue');

  /** Register takes an email or a phone, and both are sent a code. */
  const registerCta = (arrivedFromMiss && !!identifier) || canSendCodeTo(registerTyped)
    ? 'Send verification code'
    : 'Continue';

  /** The identifier, shown back on every screen 2 with a way to change it. */
  const renderWhoChip = (changeable = true) => (
    <div className="login-who" data-testid="login-who">
      <span className="login-who-value">{otpPhone ? `+91 ${otpPhone}` : identifier}</span>
      {changeable && (
        <a href="#" data-testid="login-change-identifier"
           onClick={(e) => { e.preventDefault(); switchToIdentify(); }}>
          Change
        </a>
      )}
    </div>
  );

  const renderCodeField = () => (
    <div className="form-group">
      <label htmlFor="otp">Verification Code</label>
      <input
        type="text" id="otp" name="otp" required
        placeholder="Enter 6-digit code"
        maxLength={6}
        pattern="[0-9]{6}"
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus
        className="otp-input"
        data-testid="otp-input"
      />
    </div>
  );

  const renderResend = () => (
    <div className="resend-link">
      {cooldown > 0 ? (
        <span className="resend-cooldown">Resend in {cooldown}s</span>
      ) : (
        <a href="#" onClick={(e) => { e.preventDefault(); handleResend(); }}>Resend code</a>
      )}
    </div>
  );

  // ── Render form based on step ──────────────────────────────────────

  const renderForm = () => {
    // One choke point rather than a guard per entry point — `?register=1`, the
    // footer link, and an identifier that turns out to be new all land here.
    if (selfRegistrationBlocked && step.startsWith('register')) {
      return (
        <div className="login-blocked-register" data-testid="self-registration-blocked">
          <p>
            {tenant
              ? `Registration isn't open here. Ask ${tenant.name} for an invite`
              : 'Registration isn\'t open here'}
            {tenant?.sharedAppUrl ? ', or ' : '.'}
          </p>
          {tenant?.sharedAppUrl && (
            <p>
              <a href={tenant.sharedAppUrl} data-testid="self-registration-elsewhere">
                sign up at {new URL(tenant.sharedAppUrl).host}
              </a>
            </p>
          )}
          <p className="register-link">
            <a href="#" onClick={(e) => { e.preventDefault(); switchToIdentify(); }}>Back to sign in</a>
          </p>
        </div>
      );
    }

    switch (step) {
      case 'identify':
        return (
          <form key={step} onSubmit={handleIdentifySubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="identifier">
                {showPhone ? 'Email, phone or login ID' : 'Email or login ID'}
              </label>
              {/* NOT type="email". Sign-in takes an identifier, which is an
                  address for most people, a phone number for some, and an
                  issued id (`1042.teoler`) for someone a school provisioned
                  without one. The browser's own validation on type="email"
                  rejects those before the request is ever made — the field, not
                  the API, would decide who can sign in.
                  See docs/FeatureDesigns/roster-accounts-and-login-identity.md. */}
              <input
                type="text" id="identifier" name="identifier" required
                placeholder={showPhone ? 'you@example.com or 9876543210' : 'Enter your email or login ID'}
                autoComplete="username"
                autoFocus={!defaultEmail}
                defaultValue={defaultEmail || identifier}
                disabled={emailDisabled}
                onChange={(e) => setTyped(e.target.value)}
                data-testid="email-input"
              />
            </div>
            {identifierHint && (
              <p className="login-identifier-hint" data-testid="identifier-hint">{identifierHint}</p>
            )}
            <Button type="submit" fullWidth loading={isLoading} data-testid="login-submit">
              {identifyCta}
            </Button>
          </form>
        );

      case 'password':
        return (
          <form key={step} onSubmit={handlePasswordSubmit} className="login-form">
            {renderWhoChip()}
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password" id="password" name="password" required
                placeholder="Enter your password"
                autoComplete="current-password"
                autoFocus
                data-testid="password-input"
              />
            </div>
            {onResetPassword && onSendOtp && identifier.includes('@') && (
              <div className="forgot-password-link">
                <a href="#" onClick={(e) => { e.preventDefault(); startForgot(); }}>
                  Forgot password?
                </a>
              </div>
            )}
            <Button type="submit" fullWidth loading={isLoading} data-testid="login-submit">
              Sign In
            </Button>
            {/* Only when we actually hold the number. An account can have a
                verified phone while the person signed in with their email —
                and identify does not return the phone, because handing a number
                to an unauthenticated caller would leak it. */}
            {methods.includes('otp') && showPhone && otpPhone.length === 10 && (
              <div className="phone-toggle-link">
                <a href="#" data-testid="use-code-instead" onClick={async (e) => {
                  e.preventDefault();
                  clearError(); setSuccess(null);
                  setIsLoading(true);
                  try {
                    await onSendOtp?.(otpPhone, 'login', 'phone');
                    setStep('otp');
                    startCooldown();
                  } catch (err) {
                    showError(err, 'Failed to send verification code');
                  } finally {
                    setIsLoading(false);
                  }
                }}>
                  Send me a one-time code instead
                </a>
              </div>
            )}
          </form>
        );

      case 'otp':
        return (
          <form key={step} onSubmit={handleOtpSubmit} className="login-form">
            {renderWhoChip()}
            {renderCodeField()}
            <Button type="submit" fullWidth loading={isLoading} data-testid="login-submit">
              Sign In
            </Button>
            {renderResend()}
            {methods.includes('password') && (
              <div className="phone-toggle-link">
                <a href="#" data-testid="use-password-instead"
                   onClick={(e) => { e.preventDefault(); clearError(); setSuccess(null); setStep('password'); }}>
                  Use your password instead
                </a>
              </div>
            )}
          </form>
        );

      case 'provider':
        return (
          <div key={step} className="login-form">
            {renderWhoChip()}
            <p className="otp-info">
              {methods.includes('apple')
                ? 'You created this account with Apple, so it has no password.'
                : 'You created this account with Google, so it has no password.'}
            </p>
          </div>
        );

      case 'register-identifier': {
        /* Two ways to be on this screen, and they are not the same screen.
         *
         * Chosen — "Register here" — is an empty form: nothing is known yet.
         *
         * Reached from a miss is an ANSWER to something the person just asked.
         * They typed an identifier expecting to sign in, so the screen owes
         * them the reason it changed, and the identifier reads back as the
         * value it is rather than as a field they must retype. Editing it is
         * going back, which is what Change does — the same affordance every
         * other screen 2 carries. */
        const fromMiss = arrivedFromMiss && !!identifier;
        return (
          <form key={step} onSubmit={handleRegisterSendOtp} className="login-form">
            {fromMiss ? (
              <>
                <p className="login-notice" data-testid="register-not-found">
                  No account found for this {otpPhone ? 'number' : 'sign-in'}. Create one?
                </p>
                {renderWhoChip()}
              </>
            ) : (
              <div className="form-group">
                <label htmlFor="identifier">{showPhone ? 'Email or phone' : 'Email'}</label>
                {/* type="text" even here: with phone signup on, a 10-digit number
                    is a valid value and type="email" would reject it in the
                    browser before the request. */}
                <input
                  type={showPhone ? 'text' : 'email'} id="identifier" name="identifier" required
                  placeholder={showPhone ? 'you@example.com or 9876543210' : 'Enter your email'}
                  autoComplete={showPhone ? 'username' : 'email'}
                  autoFocus={!defaultEmail}
                  defaultValue={defaultEmail}
                  disabled={emailDisabled}
                  onChange={(e) => setRegisterTyped(e.target.value)}
                  data-testid="email-input"
                />
              </div>
            )}
            <Button type="submit" fullWidth loading={isLoading} data-testid="register-send-otp">
              {registerCta}
            </Button>
          </form>
        );
      }

      case 'register-otp':
        return (
          <form key={step} onSubmit={handleRegisterVerifyOtp} className="login-form">
            <p className="otp-info">
              Code sent to <strong>{otpPhone ? `+91 ${otpPhone}` : identifier}</strong>
            </p>
            {!otpPhone && <p className="otp-hint">Check your spam folder if it hasn't arrived.</p>}
            {renderCodeField()}
            <Button type="submit" fullWidth loading={isLoading}>
              Verify Code
            </Button>
            {renderResend()}
          </form>
        );

      case 'register-details':
        return (
          <form key={step} onSubmit={handleRegisterSubmit} className="login-form" autoComplete="off">
            <p className="otp-info">
              Signing up as <strong>{otpPhone ? `+91 ${otpPhone}` : identifier}</strong>
            </p>
            <div className="form-group">
              <label htmlFor="reg-name">Full Name</label>
              <input
                type="text" id="reg-name" name="name" required
                placeholder="Enter your name"
                autoComplete="off"
                autoFocus
                data-testid="name-input"
              />
            </div>
            {/* Email signups set a password; phone signups do not get the
                option. A phone account signs in with a one-time code, and
                offering an optional password at signup asked people to make a
                decision about a mechanism they had not met yet. One can still
                be set later from Settings. */}
            {!otpPhone && (
              <div className="form-group">
                <label htmlFor="reg-password">Password</label>
                <input
                  type="password" id="reg-password" name="password" required
                  placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
                  autoComplete="new-password"
                  minLength={MIN_PASSWORD_LENGTH}
                  data-testid="password-input"
                />
              </div>
            )}
            <Button type="submit" fullWidth loading={isLoading} data-testid="login-submit">
              Create Account
            </Button>
          </form>
        );

      case 'forgot-otp':
        return (
          <form key={step} onSubmit={handleForgotOtpSubmit} className="login-form">
            <p className="otp-info">
              Code sent to <strong>{otpPhone ? `+91 ${otpPhone}` : identifier}</strong>
            </p>
            {!otpPhone && <p className="otp-hint">Check your spam folder if it hasn't arrived.</p>}
            {renderCodeField()}
            <Button type="submit" fullWidth loading={isLoading}>
              Verify Code
            </Button>
            {renderResend()}
          </form>
        );

      case 'forgot-password':
        return (
          <form key={step} onSubmit={handleResetPasswordSubmit} className="login-form" autoComplete="off">
            <div className="form-group">
              <label htmlFor="new-password">New Password</label>
              <input
                type="password" id="new-password" name="password" required
                placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
                autoComplete="new-password"
                minLength={MIN_PASSWORD_LENGTH}
                autoFocus
              />
            </div>
            <div className="form-group">
              <label htmlFor="confirm-password">Confirm Password</label>
              <input
                type="password" id="confirm-password" name="confirmPassword" required
                placeholder="Re-enter your password"
                autoComplete="new-password"
                minLength={MIN_PASSWORD_LENGTH}
              />
            </div>
            <Button type="submit" fullWidth loading={isLoading}>
              Reset Password
            </Button>
          </form>
        );
    }
  };

  // ── Footer resolution ──────────────────────────────────────────────

  const renderFooter = () => {
    if (step === 'identify') {
      return (
        footer ?? (
          onRegister && (
            <p className="register-link">
              New to BuzzBrain? <a href="#" onClick={(e) => { e.preventDefault(); switchToRegister(); }}>Register here</a>
            </p>
          )
        )
      );
    }
    if (isRegisterFlow) {
      return (
        <>
          <p className="register-link">
            Already have an account? <a href="#" onClick={(e) => { e.preventDefault(); switchToIdentify(); }}>Sign in</a>
          </p>
          {step === 'register-identifier' && registerFooter}
        </>
      );
    }
    // Every other screen 2 already carries "Change" on the identifier chip; the
    // forgot and provider screens do not, so they get the way back here.
    if (isForgotFlow || step === 'provider') {
      return (
        <p className="register-link">
          <a href="#" onClick={(e) => { e.preventDefault(); switchToIdentify(); }}>Back to sign in</a>
        </p>
      );
    }
    return null;
  };

  const isDev = isDevEnvironment();

  /** Google and Apple belong on the first screen, and on the screen that exists
   *  precisely because one of them owns the account. */
  const showProviderButtons = step === 'identify' || step === 'register-identifier' || step === 'provider';

  return (
    <div className={`login-page${isDev ? ' login-page--dev' : ''}`}>
      <div className={`login-card${isDev ? ' login-card--dev' : ''}`}>
        {isDev && (
          <p className="login-env-banner" role="status" data-testid="dev-env-banner">
            This is Dev Environment
          </p>
        )}
        {topLink && <div className="login-top-link">{topLink}</div>}
        <div className="login-header">
          {branding.brandName ? (
            <>
              {/* No stand-in mark for a tenant that has not uploaded one — a
                  default logo over their name misattributes it just as badly. */}
              {branding.logoUrl && (
                <img src={branding.logoUrl} alt={branding.brandName} className="login-logo login-tenant-logo" />
              )}
              <h1>{branding.brandName}</h1>
              <span className="login-brand-host">BuzzBrain</span>
            </>
          ) : (
            <>
              <img src={appIcon} alt="BuzzBrain" className="login-logo" width="64" height="64" />
              {/* A sentence-length title gets a sentence-sized heading. */}
              <h1 className={(isRegisterFlow ? registerTitle ?? title : title) ? 'login-title-custom' : undefined}>
                {(isRegisterFlow ? registerTitle ?? title : title) ?? 'BuzzBrain'}
              </h1>
            </>
          )}
          {resolvedSubtitle && <p className="login-subtitle">{resolvedSubtitle}</p>}
        </div>

        {headerExtra}

        {error && errorCode === 'WRONG_APP' && errorSignInUrl ? (
          // Not a failure: they signed in fine, at the other app's door.
          <div className="login-notice login-wrong-app" data-testid="login-wrong-app">
            <p className="login-wrong-app-title">{error}</p>
            <a href={errorSignInUrl} className="login-wrong-app-continue" data-testid="login-wrong-app-continue">
              {errorSignInLabel ?? 'Continue'} →
            </a>
          </div>
        ) : error && (
          <div className="login-error" data-testid="login-error">
            {error}
            {errorCode === 'WRONG_TENANT_URL' && errorSignInUrl && (
              <>
                {' '}
                <a href={errorSignInUrl} data-testid="login-error-signin-link">
                  Sign in at {new URL(errorSignInUrl).host}
                </a>
              </>
            )}
            {errorCode === 'USER_NOT_FOUND' && onRegister && !selfRegistrationBlocked && (
              <>
                {' '}
                <a
                  href="#"
                  data-testid="login-error-register-link"
                  onClick={(e) => { e.preventDefault(); switchToRegister(); }}
                >
                  Register Now
                </a>
              </>
            )}
          </div>
        )}
        {success && <div className="login-success" data-testid="login-success">{success}</div>}

        {renderForm()}

        {showProviderButtons && (gsiClientId || onGoogleNativeLogin || onAppleLogin) && (
          <>
            {step !== 'provider' && (
              <div className="login-divider">
                <span>or</span>
              </div>
            )}
            {/* Google's official rendered button — hosted inside a Google
              * iframe; styled by Google with the theme/size/shape options
              * passed in `useGoogleSignIn`. Brand-color matching would
              * require switching to the OAuth code flow + a backend
              * exchange endpoint. Suppressed in the shell (gsiClientId==''),
              * where the native Google button below is used instead. */}
            {gsiClientId && <div className="google-signin-container" ref={googleButtonRef} />}
            {onGoogleNativeLogin && (
              <button
                type="button"
                className="google-signin-button"
                onClick={handleGoogleNative}
                disabled={isLoading}
                data-testid="google-signin-native"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                  <path fill="#4285F4" d="M23.52 12.27c0-.82-.07-1.6-.2-2.36H12v4.47h6.47a5.53 5.53 0 0 1-2.4 3.63v3.01h3.88c2.27-2.09 3.57-5.17 3.57-8.75z" />
                  <path fill="#34A853" d="M12 24c3.24 0 5.96-1.08 7.95-2.91l-3.88-3.01c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.11A12 12 0 0 0 12 24z" />
                  <path fill="#FBBC05" d="M5.27 14.28a7.2 7.2 0 0 1 0-4.56V6.61H1.29a12 12 0 0 0 0 10.78l3.98-3.11z" />
                  <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.44-3.44A11.5 11.5 0 0 0 12 0 12 12 0 0 0 1.29 6.61l3.98 3.11C6.22 6.86 8.87 4.75 12 4.75z" />
                </svg>
                <span>Sign in with Google</span>
              </button>
            )}
            {onAppleLogin && (
              <button
                type="button"
                className="apple-signin-button"
                onClick={handleAppleLogin}
                disabled={isLoading}
                data-testid="apple-signin"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                  <path d="M16.365 1.43c0 1.14-.44 2.1-1.24 2.9-.86.86-2.02 1.5-3.19 1.41-.14-1.11.44-2.28 1.19-3.01.85-.83 2.28-1.44 3.24-1.3zM20.5 17.2c-.55 1.27-.82 1.84-1.53 2.96-.99 1.57-2.39 3.52-4.12 3.53-1.54.02-1.93-1-4.02-.99-2.09.01-2.52 1.01-4.06.99-1.73-.02-3.05-1.78-4.04-3.35C-.02 15.4-.3 9.9 1.62 7.16c1.09-1.57 2.83-2.5 4.47-2.5 1.67 0 2.72 1.01 4.1 1.01 1.34 0 2.16-1.01 4.1-1.01 1.46 0 3.01.8 4.11 2.18-3.61 1.98-3.02 7.13 0 8.36z" />
                </svg>
                <span>Sign in with Apple</span>
              </button>
            )}
          </>
        )}

        {renderFooter()}
      </div>
    </div>
  );
}
